import { Injectable, inject } from '@angular/core';
import {
  Firestore, collection, doc, getDoc, getDocs,
  query, where, onSnapshot, serverTimestamp, DocumentData,
  DocumentReference,
  updateDoc,
  limit,
  deleteField,
  Timestamp,
} from '@angular/fire/firestore';
import { Auth, authState, User } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { Storage, ref, uploadBytes, getDownloadURL } from '@angular/fire/storage';
import { catchError, combineLatest, firstValueFrom, map, Observable, of, startWith, switchMap } from 'rxjs';
import {
  Booking,
  compareBookingsByEventAsc,
  SPACE_DELETE_BLOCKING_STATUSES,
  SecurityDepositReceivedMethod,
  SecurityDepositMethod,
  isSecurityDepositSecured,
} from '../models/booking.model';
import { OwnerSettings } from '../models/user.model';
import { BOOKING_RECEIPT_CACHE_CONTROL } from '../shared/storage/storage-cache.constants';
import { ScheduleException } from '../models/asset.model';
import { fromCollectionSnapshot } from '../shared/firestore/live-query';
import { FirebaseAwareService } from '../shared/firestore/firebase-aware.service';
import { computeFormalizationDeadline } from '../shared/booking/formalization-deadline.utils';

export interface CheckoutAmountQuote {
  rentalCents: number;
  commissionGrossCents: number;
  applicationFeeCents: number;
  payNowCents: number;
  pendingBalanceCents: number;
  ownerEntityType: 'persona_fisica' | 'persona_juridica';
  ownerActsAsBusiness: boolean;
  vatRatePercent: number;
  irpfRatePercent: number;
  platformIssuerType: 'self_employed' | 'company';
}

/**
 * User-side booking service.
 *
 * Companion to {@link AssetService}'s owner-side calendar wiring; this service
 * only exposes the read/write paths a regular user needs:
 *
 *   - read live availability for a space within a date range
 *   - read schedule exceptions (one-time fixed-block releases)
 * Firestore rules enforce field-level invariants — see firestore.rules.
 * See [.doc/booking_workflow.md] for the larger booking lifecycle.
 */
@Injectable({ providedIn: 'root' })
export class BookingService extends FirebaseAwareService {
  private firestore = inject(Firestore);
  private auth      = inject(Auth);
  private storage   = inject(Storage);
  private functions = inject(Functions);

  private static readonly RECEIPT_MAX_BYTES = 10 * 1024 * 1024;
  private static readonly RECEIPT_TYPES = new Set([
    'application/pdf',
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/heic',
  ]);

  /** Uploads deposit receipt (PDF/image) for a PENDING_FORMALIZATION booking. */
  async uploadDepositReceipt(bookingId: string, file: File): Promise<string> {
    const uid = await this.currentUid();
    if (!uid) throw new Error('Not authenticated');
    if (!BookingService.RECEIPT_TYPES.has(file.type)) {
      throw new Error('Formato no válido. Usa PDF, JPG, PNG o WEBP.');
    }
    if (file.size > BookingService.RECEIPT_MAX_BYTES) {
      throw new Error('El archivo supera el límite de 10 MB.');
    }

    const ext = file.type === 'application/pdf' ? 'pdf'
      : file.type === 'image/png' ? 'png'
      : file.type === 'image/webp' ? 'webp'
      : 'jpg';
    const path = `bookings/${bookingId}/deposit-receipt/${uid}_${Date.now()}.${ext}`;
    const storageRef = this.inCtx(() => ref(this.storage, path));
    await this.inCtx(() => uploadBytes(storageRef, file, {
      contentType: file.type,
      cacheControl: BOOKING_RECEIPT_CACHE_CONTROL,
    }));
    return this.inCtx(() => getDownloadURL(storageRef));
  }

  /**
   * Step 2: contract snapshot + receipt URL (both required).
   * Transitions PENDING_FORMALIZATION → PENDING_VERIFICATION.
   */
  /**
   * Reads titular, IBAN and Bizum from the space owner's profile (Ajustes → Contacto y cobro).
   * Callable — guests cannot read `/users/{ownerUid}` directly in Firestore rules.
   */
  async fetchOwnerPaymentDetails(bookingId: string): Promise<Partial<OwnerSettings>> {
    const call = this.inCtx(() =>
      httpsCallable<{ bookingId: string }, Partial<OwnerSettings>>(
        this.functions,
        'getBookingOwnerPaymentDetails',
      ),
    );
    const res = await call({ bookingId });
    return res.data;
  }

  async completeFormalization(params: {
    bookingId: string;
    userLegalName: string;
    userDni: string;
    userPhone: string;
    depositReceiptUrl?: string;
    fastTrackBizum?: boolean;
    contractAccepted: boolean;
    userAgent?: string;
  }): Promise<void> {
    const call = this.inCtx(() => httpsCallable(this.functions, 'completeBookingFormalization'));
    await call(params);
  }

  /**
   * Guest cancels during Step 2 window (PENDING_FORMALIZATION only).
   * Slot is released; platform management fee is not refunded.
   */
  async cancelFormalizationBooking(bookingId: string): Promise<void> {
    const call = this.inCtx(() =>
      httpsCallable<{ bookingId: string }, { ok: true }>(
        this.functions,
        'cancelBookingByUser',
      ),
    );
    await call({ bookingId });
  }

  /** Resolves the signed-in user id after the Auth state has hydrated. */
  private async currentUid(): Promise<string> {
    const user = await firstValueFrom(authState(this.auth) as Observable<User | null>);
    return user?.uid ?? '';
  }

  /**
   * Live stream of calendar-blocking bookings for a space within a date range.
   *
   * Reads from the PII-free availability mirror at
   * `/assets/{spaceId}/availability/{bookingId}` (maintained by the
   * `onBookingAvailabilityMirror` Cloud Function). Each document contains
   * ONLY `{ spaceId, date, startTime, endTime, status }` — no guest name,
   * userId, totalPrice, or payment details (P0.6 — GDPR compliance).
   *
   * The returned objects are typed as `Booking` for compatibility with
   * downstream schedule-occupancy utilities, which only use the above fields.
   */
  getAvailability(spaceId: string, dateRange: { start: string; end: string }): Observable<Booking[]> {
    const q = this.inCtx(() => query(
      collection(this.firestore, `assets/${spaceId}/availability`),
      where('date', '>=', dateRange.start),
      where('date', '<=', dateRange.end),
    ));
    return fromCollectionSnapshot<Booking>(this.injector, q);
  }

  /** Live stream of one-time schedule exceptions for a space within a range. */
  getExceptions(spaceId: string, dateRange: { start: string; end: string }): Observable<ScheduleException[]> {
    const q = this.inCtx(() => query(
      collection(this.firestore, `assets/${spaceId}/scheduleExceptions`),
      where('date', '>=', dateRange.start),
      where('date', '<=', dateRange.end),
    ));
    return fromCollectionSnapshot<ScheduleException>(this.injector, q);
  }

  /** Confirms that the owner has reviewed the receipt and received funds. */
  async ownerConfirmPayment(bookingId: string): Promise<void> {
    const uid = await this.currentUid();
    if (!uid) throw new Error('Not authenticated');

    const bookingRef = this.inCtx(() => doc(this.firestore, 'bookings', bookingId));
    const bookingSnap = await this.inCtx(() => getDoc(bookingRef));
    const booking = bookingSnap.data() as Booking | undefined;
    if (!booking || !this.bookingBelongsToOwner(booking, uid)) {
      throw new Error('Booking not found');
    }
    if (booking.status !== 'PENDING_VERIFICATION') {
      throw new Error('Booking is not awaiting owner verification');
    }

    await this.inCtx(() => updateDoc(bookingRef, {
      status: 'CONFIRMED',
      ownerConfirmedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }));
  }

  /**
   * Live stream of a single booking document.
   * Emits `null` when the document does not exist.
   */
  watchBooking(bookingId: string): Observable<Booking | null> {
    return new Observable<Booking | null>(subscriber => {
      const unsub = this.inCtx(() => {
        const docRef = doc(this.firestore, 'bookings', bookingId) as DocumentReference<DocumentData>;
        return onSnapshot(
          docRef,
          snap => subscriber.next(snap.exists() ? ({ id: snap.id, ...snap.data() } as Booking) : null),
          err  => subscriber.error(err),
        );
      });
      return unsub;
    });
  }

  /**
   * Live stream of a booking resolved from a Stripe checkout session id.
   * Scoped by userId so the query satisfies security rules and never
   * leaks records across users.
   */
  watchBookingByCheckoutSession(sessionId: string, userId: string): Observable<Booking | null> {
    return new Observable<Booking | null>(subscriber => {
      const unsub = this.inCtx(() => {
        const q = query(
          collection(this.firestore, 'bookings'),
          where('userId', '==', userId),
          where('stripeCheckoutSessionId', '==', sessionId),
          limit(1),
        );
        return onSnapshot(
          q,
          snap => {
            if (snap.empty) {
              subscriber.next(null);
              return;
            }
            const docSnap = snap.docs[0];
            subscriber.next({ id: docSnap.id, ...docSnap.data() } as Booking);
          },
          err => subscriber.error(err),
        );
      });
      return unsub;
    });
  }

  /**
   * Idempotently creates the Firestore booking from a paid Stripe session when the
   * webhook is delayed or missing (e.g. local dev). Safe to call while listening.
   */
  async syncBookingFromCheckoutSession(sessionId: string): Promise<{ bookingId: string; status: string }> {
    const call = this.inCtx(() =>
      httpsCallable<
        { sessionId: string },
        { bookingId: string; status: string; outcome: string }
      >(this.functions, 'syncBookingFromCheckoutSession'),
    );
    const { data } = await call({ sessionId: sessionId.trim() });
    return { bookingId: data.bookingId, status: data.status };
  }

  /** Effective checkout mode and server-side amount quote for a space owner. */
  async getOwnerCheckoutMode(
    ownerId: string,
    totalPrice?: number,
  ): Promise<{
    checkoutMode: 'MANUAL' | 'STRIPE';
    commissionRatePct: number;
    quote?: CheckoutAmountQuote;
  }> {
    const call = this.inCtx(() =>
      httpsCallable<
        { ownerId: string; totalPrice?: number },
        {
          checkoutMode: 'MANUAL' | 'STRIPE';
          commissionRatePct: number;
          quote?: CheckoutAmountQuote;
        }
      >(this.functions, 'getOwnerCheckoutMode'),
    );
    const payload: { ownerId: string; totalPrice?: number } = { ownerId };
    if (typeof totalPrice === 'number' && Number.isFinite(totalPrice) && totalPrice > 0) {
      payload.totalPrice = totalPrice;
    }
    const { data } = await call(payload);
    return {
      checkoutMode: data.checkoutMode === 'STRIPE' ? 'STRIPE' : 'MANUAL',
      commissionRatePct: data.commissionRatePct,
      quote: data.quote,
    };
  }

  async createCheckoutSession(request: {
    spaceId: string;
    userId: string;
    date: string;
    startTime: string;
    endTime: string;
    totalPrice: number;
    ownerId: string;
    returnOrigin?: string;
    reservationConditionsAccepted: boolean;
    conditionsVersion: string;
    userAgent?: string;
  }): Promise<{ sessionId: string; url: string }> {
    const call = this.inCtx(() =>
      httpsCallable<
        typeof request,
        { sessionId: string; url: string }
      >(this.functions, 'createCheckoutSession'),
    );
    const { data } = await call(request);
    if (!data.url) {
      throw new Error('Stripe did not return a checkout URL.');
    }
    return data;
  }

  /**
   * Live stream of all bookings where the current user is the renter (userId),
   * sorted by event date ascending (nearest upcoming first).
   * Waits for Firebase Auth to fully hydrate before subscribing to Firestore,
   * avoiding the race condition where `auth.currentUser` is null at ngOnInit.
   */
  getUserBookings(): Observable<Booking[]> {
    return (authState(this.auth) as Observable<User | null>).pipe(
      switchMap(user => {
        if (!user?.uid) return of<Booking[]>([]);
        const q = this.inCtx(() => query(
          collection(this.firestore, 'bookings'),
          where('userId', '==', user.uid),
        ));
        return fromCollectionSnapshot<Booking>(this.injector, q).pipe(
          map(rows => [...rows].sort(compareBookingsByEventAsc)),
        );
      }),
    );
  }

  /**
   * Live stream of all bookings on spaces owned by the given uid.
   * Merges ownerUid + ownerId queries for legacy documents.
   * Client-side tab/space filters avoid extra Firestore round-trips.
   */
  watchOwnerBookings(ownerUid: string): Observable<Booking[]> {
    if (!ownerUid) return of([]);
    const byUidQ = this.inCtx(() => query(
      collection(this.firestore, 'bookings'),
      where('ownerUid', '==', ownerUid),
    ));
    const byIdQ = this.inCtx(() => query(
      collection(this.firestore, 'bookings'),
      where('ownerId', '==', ownerUid),
    ));
    const byUid = fromCollectionSnapshot<Booking>(this.injector, byUidQ);
    // Legacy: some bookings store the owner uid in `ownerId` instead of `ownerUid`.
    // startWith([]) prevents combineLatest from blocking until this query resolves.
    // catchError prevents a Firestore permission/index error from killing the stream.
    const byId = fromCollectionSnapshot<Booking>(this.injector, byIdQ).pipe(
      startWith<Booking[]>([]),
      catchError(() => of<Booking[]>([])),
    );
    return combineLatest([byUid, byId]).pipe(
      map(([uidRows, idRows]) => this.mergeOwnerBookingStreams(uidRows, idRows, ownerUid)),
    );
  }

  private mergeOwnerBookingStreams(
    uidRows: Booking[],
    idRows: Booking[],
    ownerUid: string,
  ): Booking[] {
    const map = new Map<string, Booking>();
    for (const row of [...uidRows, ...idRows]) {
      if (!row.id) continue;
      map.set(row.id, {
        ...row,
        ownerUid: row.ownerUid || row.ownerId || ownerUid,
      });
    }
    return [...map.values()];
  }

  private bookingBelongsToOwner(booking: Booking, uid: string): boolean {
    return booking.ownerUid === uid || booking.ownerId === uid;
  }

  /** Owner confirms transfer receipt → CONFIRMED. */
  async ownerMarkSecurityDepositReceived(
    bookingId: string,
    method: SecurityDepositReceivedMethod,
  ): Promise<void> {
    const uid = await this.currentUid();
    if (!uid) throw new Error('Not authenticated');

    const allowed: SecurityDepositReceivedMethod[] = ['transfer', 'bizum', 'cash'];
    if (!allowed.includes(method)) {
      throw new Error('Invalid deposit received method');
    }

    const bookingRef = this.inCtx(() => doc(this.firestore, 'bookings', bookingId));
    const bookingSnap = await this.inCtx(() => getDoc(bookingRef));
    const booking = bookingSnap.data() as Booking | undefined;
    if (!booking || !this.bookingBelongsToOwner(booking, uid)) {
      throw new Error('Booking not found');
    }
    if (booking.status !== 'CONFIRMED') {
      throw new Error('Booking is not confirmed');
    }
    if (booking.securityDepositReceivedAt) {
      throw new Error('Security deposit already marked as received');
    }
    if (
      booking.securityDepositStatus === 'submitted'
      && booking.securityDepositMethod
      && booking.securityDepositMethod !== 'cash'
    ) {
      throw new Error('Use deposit receipt confirmation for this booking');
    }

    await this.inCtx(() => updateDoc(bookingRef, {
      securityDepositReceivedAt: serverTimestamp(),
      securityDepositReceivedMethod: method,
      securityDepositStatus: 'confirmed',
      updatedAt: serverTimestamp(),
    }));
  }

  async ownerConfirmSecurityDeposit(bookingId: string): Promise<void> {
    const call = this.inCtx(() =>
      httpsCallable<{ bookingId: string }, { ok: true }>(
        this.functions,
        'ownerConfirmSecurityDeposit',
      ),
    );
    await call({ bookingId });
  }

  /** Uploads security deposit receipt (CONFIRMED + awaiting_guest). */
  async uploadSecurityDepositReceipt(bookingId: string, file: File): Promise<string> {
    const uid = await this.currentUid();
    if (!uid) throw new Error('Not authenticated');
    if (!BookingService.RECEIPT_TYPES.has(file.type)) {
      throw new Error('Formato no válido. Usa PDF, JPG, PNG o WEBP.');
    }
    if (file.size > BookingService.RECEIPT_MAX_BYTES) {
      throw new Error('El archivo supera el límite de 10 MB.');
    }

    const ext = file.type === 'application/pdf' ? 'pdf'
      : file.type === 'image/png' ? 'png'
      : file.type === 'image/webp' ? 'webp'
      : 'jpg';
    const path = `bookings/${bookingId}/security-deposit-receipt/${uid}_${Date.now()}.${ext}`;
    const storageRef = this.inCtx(() => ref(this.storage, path));
    await this.inCtx(() => uploadBytes(storageRef, file, {
      contentType: file.type,
      cacheControl: BOOKING_RECEIPT_CACHE_CONTROL,
    }));
    return this.inCtx(() => getDownloadURL(storageRef));
  }

  async submitSecurityDeposit(params: {
    bookingId: string;
    method: Exclude<SecurityDepositMethod, 'card_hold'>;
    securityDepositReceiptUrl?: string;
  }): Promise<void> {
    const call = this.inCtx(() =>
      httpsCallable<
        { bookingId: string; method: string; securityDepositReceiptUrl?: string },
        { ok: true; status: string }
      >(this.functions, 'submitSecurityDeposit'),
    );
    await call(params);
  }

  async createDepositCardHoldSession(bookingId: string): Promise<{ sessionId: string; url: string }> {
    const call = this.inCtx(() =>
      httpsCallable<
        { bookingId: string; returnOrigin?: string },
        { sessionId: string; url: string }
      >(this.functions, 'createDepositCardHoldSession'),
    );
    const origin = typeof window !== 'undefined' ? window.location.origin : undefined;
    const res = await call({ bookingId, returnOrigin: origin });
    return res.data;
  }

  async ownerCaptureDepositCardHold(bookingId: string, amountCents: number): Promise<void> {
    const call = this.inCtx(() =>
      httpsCallable<{ bookingId: string; amountCents: number }, { ok: true }>(
        this.functions,
        'captureDepositCardHold',
      ),
    );
    await call({ bookingId, amountCents });
  }

  async ownerReleaseDepositCardHold(bookingId: string): Promise<void> {
    const call = this.inCtx(() =>
      httpsCallable<{ bookingId: string }, { ok: true }>(
        this.functions,
        'releaseDepositCardHold',
      ),
    );
    await call({ bookingId });
  }

  async syncDepositFromCheckoutSession(sessionId: string, bookingId?: string): Promise<{ bookingId: string }> {
    const call = this.inCtx(() =>
      httpsCallable<{ sessionId: string; bookingId?: string }, { ok: true; bookingId: string }>(
        this.functions,
        'syncDepositFromCheckoutSession',
      ),
    );
    const res = await call({ sessionId, ...(bookingId ? { bookingId } : {}) });
    return { bookingId: res.data.bookingId };
  }

  /**
   * Owner starts event day → ACTIVE.
   * Default: requires securityDepositReceivedAt.
   * skipDepositConfirmation: audit override (no deposit return flow later).
   */
  async ownerActivateBooking(
    bookingId: string,
    options?: { skipDepositConfirmation?: boolean },
  ): Promise<void> {
    const uid = await this.currentUid();
    if (!uid) throw new Error('Not authenticated');

    const bookingRef = this.inCtx(() => doc(this.firestore, 'bookings', bookingId));
    const bookingSnap = await this.inCtx(() => getDoc(bookingRef));
    const booking = bookingSnap.data() as Booking | undefined;
    if (!booking || !this.bookingBelongsToOwner(booking, uid)) {
      throw new Error('Booking not found');
    }
    if (booking.status !== 'CONFIRMED') {
      throw new Error('Booking is not confirmed');
    }

    const skip = options?.skipDepositConfirmation === true;
    if (!skip && !isSecurityDepositSecured(booking)) {
      throw new Error('Mark security deposit as received before activating');
    }
    if (skip && isSecurityDepositSecured(booking)) {
      throw new Error('Deposit already received; use normal activation');
    }

    if (skip) {
      await this.inCtx(() => updateDoc(bookingRef, {
        status: 'ACTIVE',
        activatedAt: serverTimestamp(),
        securityDepositSkippedByOwnerAt: serverTimestamp(),
        securityDepositSkippedByOwnerUid: uid,
        securityDepositStatus: 'skipped_by_owner',
        updatedAt: serverTimestamp(),
      }));
      return;
    }

    await this.inCtx(() => updateDoc(bookingRef, {
      status: 'ACTIVE',
      activatedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }));
  }

  /** ACTIVE → COMPLETED when deposit was skipped at activation (no return step). */
  async ownerCompleteEventWithoutDeposit(bookingId: string): Promise<void> {
    const uid = await this.currentUid();
    if (!uid) throw new Error('Not authenticated');

    const bookingRef = this.inCtx(() => doc(this.firestore, 'bookings', bookingId));
    const bookingSnap = await this.inCtx(() => getDoc(bookingRef));
    const booking = bookingSnap.data() as Booking | undefined;
    if (!booking || !this.bookingBelongsToOwner(booking, uid)) {
      throw new Error('Booking not found');
    }
    if (booking.status !== 'ACTIVE') {
      throw new Error('Booking is not active');
    }
    if (!booking.securityDepositSkippedByOwnerAt) {
      throw new Error('Use deposit return flow for this booking');
    }

    await this.inCtx(() => updateDoc(bookingRef, {
      status: 'COMPLETED',
      updatedAt: serverTimestamp(),
    }));
  }

  /** Owner returned security deposit → COMPLETED. */
  async ownerReturnSecurityDeposit(bookingId: string): Promise<void> {
    const uid = await this.currentUid();
    if (!uid) throw new Error('Not authenticated');

    const bookingRef = this.inCtx(() => doc(this.firestore, 'bookings', bookingId));
    const bookingSnap = await this.inCtx(() => getDoc(bookingRef));
    const booking = bookingSnap.data() as Booking | undefined;
    if (!booking || !this.bookingBelongsToOwner(booking, uid)) {
      throw new Error('Booking not found');
    }
    if (booking.status !== 'ACTIVE') {
      throw new Error('Booking is not active');
    }
    if (booking.securityDepositSkippedByOwnerAt) {
      throw new Error('Deposit was skipped at activation');
    }
    if (booking.securityDepositReturnedAt) {
      throw new Error('Security deposit already returned');
    }

    await this.inCtx(() => updateDoc(bookingRef, {
      status: 'COMPLETED',
      securityDepositReturnedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    }));
  }

  /** Owner rejects deposit receipt → back to PENDING_FORMALIZATION for re-upload. */
  async ownerRejectVerification(bookingId: string, rejectionNote?: string): Promise<void> {
    const uid = await this.currentUid();
    if (!uid) throw new Error('Not authenticated');

    const bookingRef = this.inCtx(() => doc(this.firestore, 'bookings', bookingId));
    const bookingSnap = await this.inCtx(() => getDoc(bookingRef));
    const booking = bookingSnap.data() as Booking | undefined;
    if (!booking || !this.bookingBelongsToOwner(booking, uid)) {
      throw new Error('Booking not found');
    }
    if (booking.status !== 'PENDING_VERIFICATION') {
      throw new Error('Booking is not awaiting verification');
    }
    if (!booking.date || !booking.startTime) {
      throw new Error('Booking is missing schedule fields');
    }

    const nowMs = Date.now();
    const { deadlineMs, windowDays, daysUntilEvent, deadlineMode } = computeFormalizationDeadline(
      booking.date,
      booking.startTime,
      nowMs,
    );

    const note = rejectionNote?.trim().slice(0, 500);
    await this.inCtx(() => updateDoc(bookingRef, {
      status: 'PENDING_FORMALIZATION',
      depositReceiptUrl: deleteField(),
      depositReceiptSubmittedAt: deleteField(),
      depositReceiptRejectedAt: serverTimestamp(),
      ...(note ? { depositReceiptRejectionNote: note } : { depositReceiptRejectionNote: deleteField() }),
      formalizationDeadline: Timestamp.fromMillis(deadlineMs),
      formalizationWindowDays: windowDays,
      formalizationDeadlineMode: deadlineMode,
      daysUntilEventAtPayment: daysUntilEvent,
      updatedAt: serverTimestamp(),
    }));
  }

  /**
   * Owner-side live stream of all bookings on a space within a date range.
   * The ownerUid filter is required by firestore.rules for list queries.
   */
  getOwnerSpaceBookings(
    spaceId: string,
    ownerUid: string,
    dateRange: { start: string; end: string },
  ): Observable<Booking[]> {
    const q = this.inCtx(() => query(
      collection(this.firestore, 'bookings'),
      where('spaceId', '==', spaceId),
      where('ownerUid', '==', ownerUid),
      where('date', '>=', dateRange.start),
      where('date', '<=', dateRange.end),
    ));
    return fromCollectionSnapshot<Booking>(this.injector, q);
  }

  /** True when the space cannot be deleted (active or historical bookings). */
  hasDeleteBlockingBookings(spaceId: string, ownerUid: string): Promise<boolean> {
    const statuses = [...SPACE_DELETE_BLOCKING_STATUSES];
    return this.inCtx(() => {
      const bookings = collection(this.firestore, 'bookings');
      return Promise.all([
        getDocs(query(
          bookings,
          where('spaceId', '==', spaceId),
          where('ownerUid', '==', ownerUid),
          where('status', 'in', statuses),
          limit(1),
        )),
        getDocs(query(
          bookings,
          where('spaceId', '==', spaceId),
          where('ownerId', '==', ownerUid),
          where('status', 'in', statuses),
          limit(1),
        )),
      ]).then(([byUid, byOwnerId]) => !byUid.empty || !byOwnerId.empty);
    });
  }

}
